import BaseLayer from 'ol/layer/Base'
import {useCallback, useEffect, useMemo, useState} from 'react'
import { useAppSelector } from '../../common/store'
import {DEFAULT_LAYERS} from '../map/map.constants'
import {useMap} from "../map/map.hook";
import {
  FLOOD_LAYER_MIN_ZOOM,
  FLOOD_MAP_OVERLAY, FLOOD_POLYGON_COLOUR_FILL, FLOOD_POLYGON_COLOUR_STROKE, FLOOD_POLYGON_COLOUR_WIDTH,
  MAP_PROJECTION,
  RIVERINE_LAYER_NAME,
  STORM_1000,
  STORM_LAYER_1000y,
  STORM_LAYER_100y,
  STORM_SURGE
} from "../../environment";
import {getSelectedFloodLayer, getSelectedTab, StyledLayerType} from "./slice";
import TileLayer from "ol/layer/Tile";
import {TileWMS} from "ol/source";
import {useFloodData} from "./util";
import {Fill, Stroke, Style} from "ol/style";
import floodServiceAPI from "../../apis/FloodServiceAPI";
import {GeoJSON} from "ol/format";
import Collection from "ol/Collection";
import {Feature} from "ol";
import {Geometry, GeometryCollection} from "ol/geom";
import Vector from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {GeoLatte} from "../placenames-components/slice";

const style = new Style({
  stroke: new Stroke({
    color: FLOOD_POLYGON_COLOUR_STROKE,
    width: FLOOD_POLYGON_COLOUR_WIDTH,
  }),
  fill: new Fill({
    color: FLOOD_POLYGON_COLOUR_FILL,
  }),
})

const useFloodMapData = () => {
  const activeTab = useAppSelector(getSelectedTab)
  const activeFloodLayer = useAppSelector(getSelectedFloodLayer)
  const primaryMarker = useFloodData()
  const [geom, setGeom] = useState<GeoLatte[]>()
  const { layers, setLayers } = useMap()


  const buildPolygonLayer = useCallback((layerDetails: StyledLayerType) => {
    const layerFeaturesCollection = new Collection<Feature>()
    if (layerDetails) {
      const dataGeometry = layerDetails.data;
      let styledLayerFeatures: Feature<Geometry>[] = []

      let geometries: Geometry[] = []
      let geometry = (new GeoJSON()).readGeometry(dataGeometry).transform('EPSG:4326', 'EPSG:3857');
      geometries.push(geometry)
      const layerFeature = new Feature({
        fillColor: layerDetails.fillColor,
        geometry: new GeometryCollection(geometries),
        hasLocation: true
      })
      const polygonStyle = layerDetails.style.clone();

      layerFeature.setStyle(polygonStyle);

      styledLayerFeatures.push(layerFeature)

      if (styledLayerFeatures.length > 0) {
        layerFeaturesCollection.extend(styledLayerFeatures)
      }

      return new Vector({
        source: new VectorSource({
              features: layerFeaturesCollection,
            },
        ),
        style: layerDetails.style,
        className: layerDetails.className,
        properties: {
          name: layerDetails.className,
        },
      })
    }
  }, [])

  const buildFloodLayer = useCallback((layer: string) => {
    return new TileLayer({
      minZoom: FLOOD_LAYER_MIN_ZOOM,
      source: new TileWMS({
        url: FLOOD_MAP_OVERLAY,
        serverType: 'geoserver',
        projection: MAP_PROJECTION,
        params: {
          SERVICE: 'WMS',
          FORMAT: 'image/png',
          TRANSPARENT: 'true',
          LAYERS: layer,
          exceptions: 'application/vnd.ogc.se_inimage',
          SRS: MAP_PROJECTION,
        },
      }),
    })
  }, [])

  const floodLayer = useMemo(() => {
    let layer
    if (activeTab === STORM_SURGE) {
      if (activeFloodLayer === STORM_1000) {
        layer = STORM_LAYER_1000y
      } else {
        layer = STORM_LAYER_100y
      }
    } else {
      layer = RIVERINE_LAYER_NAME
    }
      return buildFloodLayer(layer)
  }, [buildFloodLayer, activeFloodLayer, activeTab])

  useEffect(() => {
    if (primaryMarker) {
      const handleHighlightGeoByLaiskey = async () => {
        try {
          const geom = await floodServiceAPI.getFloodGeoDataApi(primaryMarker.laisKey);
          if (geom) {
            setGeom(geom)
          }
        } catch (error: any) {
          setGeom(undefined);
        }
      };
      handleHighlightGeoByLaiskey()
    }

  }, [primaryMarker])

  const polygonLayer = useMemo(() => {
    if (geom) {
      const styledLayer: StyledLayerType = {
        'fillColor': FLOOD_POLYGON_COLOUR_FILL,
        'type': "flood-parcel",
        'style': style,
        'className': 'floodimpact-parcel',
        'data': geom
      }
      return buildPolygonLayer(<StyledLayerType> styledLayer)
    }
  }, [buildPolygonLayer, geom])

  useEffect(() => {
    const newLayers: BaseLayer[] = []

    if (floodLayer) {
      newLayers.push(floodLayer)
    }
    if (polygonLayer) {
      newLayers.push(polygonLayer)
    }

    setLayers ([...DEFAULT_LAYERS, ...newLayers])

  }, [floodLayer, polygonLayer])

  return {
    layers
  }
}

export { useFloodMapData }